import { useApolloClient } from '@apollo/client'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Check, Clock, Delete } from '@factorialco/f0-react/icons/app'
import {
  MyTrainingSessionsDocument,
  MyTrainingSessionsFiltersDocument,
  TrainingsSessionAttendanceStatusEnum,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useUpdateSessionAttendances } from 'modules/trainings/hooks/SessionAttendance/useUpdateSessionAttendances'
import { DEFAULT_DATA_COLLECTION_STORAGE } from 'modules/trainings/lib/collections'

interface Props {
  trainingId: string
  accessId: string
  sessionModalTo: (sessionId: string | number) => string
}

export const SessionsDataTable = ({ trainingId, accessId, sessionModalTo }: Props) => {
  const client = useApolloClient()
  const { updateSessionAttendances } = useUpdateSessionAttendances({
    refetchQueries: [MyTrainingSessionsDocument],
  })

  const handleAttendanceUpdate = async (
    sessionAttendanceId: string,
    status: TrainingsSessionAttendanceStatusEnum
  ) => {
    const result = await updateSessionAttendances({
      ids: [sessionAttendanceId],
      status: status,
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
      return
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('trainings.trainings_detail.training_details_updated'),
      })
    }
    return handled
  }

  const source = useGraphqlDataCollectionSource({
    document: MyTrainingSessionsDocument,
    mapData: (data) => data.trainings.sessionsConnection.nodes,
    itemUrl: (item) => sessionModalTo(item.id),
    itemActions: (item) => {
      const mySessionAttendance = item.sessionAttendances.find(
        (attendance) => attendance.accessId === accessId
      )

      if (!mySessionAttendance || !mySessionAttendance._policies?.edit?.can) {
        return []
      }

      return [
        {
          label: i18n.t('trainings.session_access_membership_table.not_started'),
          icon: Clock,
          onClick: () => handleAttendanceUpdate(mySessionAttendance.id, 'pending'),
        },
        {
          label: i18n.t('trainings.session_access_membership_table.completed'),
          icon: Check,
          onClick: () => handleAttendanceUpdate(mySessionAttendance.id, 'completed'),
        },
        {
          label: i18n.t('trainings.session_access_membership_table.absent'),
          icon: Delete,
          critical: true,
          onClick: () => handleAttendanceUpdate(mySessionAttendance.id, 'missing'),
        },
      ]
    },
    search: {
      enabled: true,
      sync: true,
    },
    mapVariables: ({ sortings, search }) => {
      return {
        accessId: accessId,
        search: search,
        sort: sortings?.[0],
        trainingIds: [trainingId],
      }
    },
    mapPagination: (data) => {
      return {
        total: data.trainings.sessionsConnection.totalCount ?? 0,
      }
    },
    filters: {
      startsAt: {
        type: 'in',
        label: i18n.t('trainings.sessions_data_collection.filters.start_date'),
        options: {
          options: async () => {
            const { data } = await client.query({
              query: MyTrainingSessionsFiltersDocument,
              variables: {
                accessId: accessId,
                trainingIds: [trainingId],
              },
              fetchPolicy: 'cache-first',
            })

            const startDatesArray = data.trainings.sessionsConnection.nodes.map((session) => {
              const startsAtLabel = session?.startsAt
                ? session.schedule === 'selfpaced'
                  ? moment(session.startsAt).format('L')
                  : i18n.t('trainings.sessions_data_collection.filters.start_date_label', {
                      startDate: moment(session.startsAt).format('L'),
                      startTime: moment(session.startsAt).format('LT'),
                    })
                : '-'
              return {
                label: startsAtLabel,
                value: session.startsAt,
                schedule: session.schedule,
              }
            })
            const startDateSet = new Set()
            const uniqueDates: { label: string; value: string }[] = []
            startDatesArray.forEach((item) => {
              const setItem = `${item.schedule}:${item.value}`
              if (!startDateSet.has(setItem) && item.value) {
                startDateSet.add(setItem)
                uniqueDates.push(item)
              }
            })
            return uniqueDates
          },
        },
      },
    },
    sortings: {
      name: {
        label: i18n.t('trainings.sessions_data_collection.name'),
      },
      startsAt: {
        label: i18n.t('trainings.sessions_data_collection.date'),
      },
    },
    defaultSortings: {
      field: 'startsAt',
      order: 'asc',
    },
  })

  return (
    <OneDataCollection
      id="trainings/my-sessions/v1"
      storage={DEFAULT_DATA_COLLECTION_STORAGE}
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t('trainings.trainings_table.name'),
                sorting: 'name',
                render: (item) => ({
                  type: 'text',
                  value: item.name,
                }),
              },
              {
                label: i18n.t('trainings.sessions_table.starts_at'),
                sorting: 'startsAt',
                render: (item) => ({
                  type: 'text',
                  value: item.startsAt
                    ? item.schedule === 'scheduled'
                      ? moment(item.startsAt).format('L HH:mm').toLocaleString()
                      : moment(item.startsAt).format('L').toLocaleString()
                    : '-',
                }),
              },
              {
                label: i18n.t('trainings.sessions_table.ends_at'),
                render: (item) => ({
                  type: 'text',
                  value:
                    item.schedule === 'scheduled'
                      ? item.endsAt
                        ? moment(item.endsAt).format('L HH:mm').toLocaleString()
                        : '-'
                      : item.dueDate
                        ? moment(item.dueDate).format('L').toLocaleString()
                        : '-',
                }),
              },
              {
                label: i18n.t('trainings.session_access_membership_table.attendance'),
                render: (item) => {
                  const mySessionAttendance = item.sessionAttendances.find(
                    (sessionAttendance) => sessionAttendance.accessId === accessId
                  )

                  if (!mySessionAttendance) {
                    return {
                      type: 'text',
                      value: '-',
                    }
                  }

                  const getStatusLabel = (status: string | null | undefined) => {
                    switch (status) {
                      case 'pending':
                        return i18n.t('trainings.session_access_membership_table.not_started')
                      case 'completed':
                        return i18n.t('trainings.session_access_membership_table.completed')
                      case 'missing':
                        return i18n.t('trainings.session_access_membership_table.absent')
                      case 'inprogress':
                        return i18n.t('trainings.session_access_membership_table.started')
                      default:
                        return '-'
                    }
                  }

                  const getStatusColor = (status: string | null | undefined) => {
                    switch (status) {
                      case 'pending':
                        return 'malibu'
                      case 'completed':
                        return 'viridian'
                      case 'missing':
                        return 'lilac'
                      case 'inprogress':
                        return 'smoke'
                      default:
                        return 'camel'
                    }
                  }

                  return {
                    type: 'dotTag',
                    value: {
                      label: getStatusLabel(mySessionAttendance.status),
                      color: getStatusColor(mySessionAttendance.status),
                    },
                  }
                },
              },
            ],
            frozenColumns: 1,
          },
        },
      ]}
    />
  )
}
